import {
  FinancialMaterialityLevel,
  FinancialThresholdByLevel,
  ImpactThresholdByLevel,
  MaterialityType,
  SeverityLevel,
} from '@upright/common/model/input/materiality-assessment/types';
import { FeCalculationVersion } from '@upright/common/model/output/materiality/financial-effects/computation/types';
import { safeVersionGte } from '@upright/common/utils';

export const impactMinMaxThresholds = {
  min: 0,
  max: 1, // OH: This is the theoretical max for a score
};

// OH: Arbitrary max so that the slider scale is reasonable
export const financialMinMaxThresholds = {
  min: 0,
  max: 16,
};

export const financialEffectMinMaxThresholds = {
  min: 0,
  max: 0.05,
};

export const getMaterialityThresholdLimits = (hasFe: boolean) => {
  const feThresholds = hasFe ? financialEffectMinMaxThresholds : financialMinMaxThresholds;
  return {
    [MaterialityType.IMPACT]: impactMinMaxThresholds,
    [MaterialityType.FINANCIAL]: feThresholds,
  };
};

// TODO: Find the correct productCsrdVersion, this is just my dev version
export const productCsrdVersionWithIncreasedImpactThresholds = '1.3.1234';

/**
 * Return default impact thresholds for csrd product version.
 *
 * @param productCsrdVersion Special handling for pre 1.3.1234
 * @returns A fresh array containing thresholds (not a mutable global array).
 */
export function getDefaultImpactThresholds(productCsrdVersion: string): ImpactThresholdByLevel {
  // OH: The increased thresholds introduced in the beginning of October 2025
  // TODO: Adjust to correct thresholds once received from Lisa
  const defaultImpactThresholds = {
    [SeverityLevel.NONE]: -Infinity,
    [SeverityLevel.LOW]: 0,
    [SeverityLevel.MEDIUM]: 0.22,
    [SeverityLevel.HIGH]: 0.5,
  };

  // OH: Default thresholds before the increased thresholds were introduced 1.10.2025
  const defaultImpactThresholdsPre1_3_1234 = {
    [SeverityLevel.NONE]: -Infinity,
    [SeverityLevel.LOW]: 0,
    [SeverityLevel.MEDIUM]: 0.2,
    [SeverityLevel.HIGH]: 0.5,
  };

  if (safeVersionGte(productCsrdVersion, productCsrdVersionWithIncreasedImpactThresholds)) {
    return defaultImpactThresholds;
  }

  return defaultImpactThresholdsPre1_3_1234;
}

/**
 * Default financial materiality score thresholds.
 *
 * The `feCalculationVersion` discriminator was introduced by PLT-3860 so V2
 * (FE 2.0) thresholds can diverge from V1 once the V2 score scale is
 * recalibrated. The values are intentionally identical between V1 and V2 for
 * the v0 milestone — Jannica's plan is to change them later, and pinning the
 * call sites now means the migration is a one-line change here rather than
 * touching every caller.
 *
 * Callers that don't have a calculation version available (e.g. legacy code,
 * unit tests) can omit the argument and get the V1 defaults.
 */
export function getDefaultFinancialThresholds(
  feCalculationVersion?: FeCalculationVersion | null,
): FinancialThresholdByLevel {
  if (feCalculationVersion === FeCalculationVersion.V2) {
    return getDefaultFinancialThresholdsV2();
  }
  return getDefaultFinancialThresholdsV1();
}

function getDefaultFinancialThresholdsV1(): FinancialThresholdByLevel {
  return {
    [FinancialMaterialityLevel.NONE]: -Infinity,
    [FinancialMaterialityLevel.LOW]: 0,
    [FinancialMaterialityLevel.MEDIUM]: 8,
    [FinancialMaterialityLevel.HIGH]: 12,
  };
}

function getDefaultFinancialThresholdsV2(): FinancialThresholdByLevel {
  // Intentionally the same as V1 for the v0 milestone (PLT-3860). The V2
  // score has the same nominal range (× 1600 normalization in
  // `calculateFmScoreV2`), so the threshold band is consistent with V1.
  // PLT will revisit these once the FE 2.0 score is empirically tuned.
  return {
    [FinancialMaterialityLevel.NONE]: -Infinity,
    [FinancialMaterialityLevel.LOW]: 0,
    [FinancialMaterialityLevel.MEDIUM]: 8,
    [FinancialMaterialityLevel.HIGH]: 12,
  };
}

export function getDefaultFinancialEffectThresholds(): FinancialThresholdByLevel {
  return {
    [FinancialMaterialityLevel.NONE]: -Infinity,
    [FinancialMaterialityLevel.LOW]: 0,
    [FinancialMaterialityLevel.MEDIUM]: 0.005,
    [FinancialMaterialityLevel.HIGH]: 0.0225,
  };
}
